/**
 * 열화상 온도 텍스트 → dataMap[y][x] 파서.
 *
 * 정상 CSV: 행=imgHeight, 열=imgWidth (예: 512행 × 640열).
 * 줄바꿈이 어긋나 512열처럼 보여도, flat 토큰 순서가 row-major(가로 imgWidth)이면
 * expectedWidth로 reshape 하면 복구된다. (단순 행렬 전치로는 복구 안 됨)
 */

/**
 * @param {string} text
 * @returns {number[]}
 */
export function extractThermalTemperatureValues(text) {
  if (typeof text !== 'string' || text.length === 0) {
    return [];
  }
  const tokens = text.split(/[;\n\r]+/);
  const values = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token === '' || token == null) {
      continue;
    }
    const v = parseFloat(token);
    if (!Number.isNaN(v)) {
      values.push(v);
    }
  }
  return values;
}

/**
 * flat 값을 row-major 로 dataMap 구성
 * @param {number[]} values
 * @param {number} width
 * @param {number} height
 * @returns {number[][]}
 */
export function reshapeTemperatureValues(values, width, height) {
  const w = Number(width);
  const h = Number(height);
  const dataMap = new Array(h);
  for (let y = 0; y < h; y += 1) {
    const row = new Array(w);
    const base = y * w;
    for (let x = 0; x < w; x += 1) {
      row[x] = values[base + x];
    }
    dataMap[y] = row;
  }
  return dataMap;
}

/**
 * 줄 단위 파싱 (레거시 / expected size 없을 때).
 * 끝의 빈 토큰(trailing `;`)만 무시하고, 예전 `length-1` 일괄 절삭은 하지 않는다.
 *
 * @param {string} text
 * @returns {{ dataMap: number[][], width: number, height: number }}
 */
export function parseTemperatureGridByLines(text) {
  const lines = String(text).split(/\r?\n/);
  const dataMap = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (line == null || line === '') {
      continue;
    }
    const parts = line.split(';');
    const row = [];
    for (let j = 0; j < parts.length; j += 1) {
      const part = parts[j];
      if (part === '' || part == null) {
        continue;
      }
      const v = parseFloat(part);
      if (!Number.isNaN(v)) {
        row.push(v);
      }
    }
    if (row.length > 0) {
      dataMap.push(row);
    }
  }
  const height = dataMap.length;
  const width = height > 0 ? dataMap[0].length : 0;
  return { dataMap, width, height };
}

/**
 * @param {string} text
 * @param {{ expectedWidth?: number, expectedHeight?: number }} [opts]
 * @returns {{
 *   dataMap: number[][],
 *   width: number,
 *   height: number,
 *   mode: 'reshape' | 'lines',
 *   valueCount: number,
 *   min: number,
 *   max: number,
 *   average: number
 * } | null}
 */
export function parseTemperatureGridText(text, opts = {}) {
  if (typeof text !== 'string' || text.length === 0) {
    return null;
  }

  const expectedWidth = Number(opts.expectedWidth);
  const expectedHeight = Number(opts.expectedHeight);
  const values = extractThermalTemperatureValues(text);
  const valueCount = values.length;

  let dataMap;
  let width;
  let height;
  let mode;

  const canReshape =
    Number.isFinite(expectedWidth) &&
    expectedWidth > 0 &&
    Number.isFinite(expectedHeight) &&
    expectedHeight > 0 &&
    valueCount === expectedWidth * expectedHeight;

  if (canReshape) {
    width = expectedWidth;
    height = expectedHeight;
    dataMap = reshapeTemperatureValues(values, width, height);
    mode = 'reshape';
  } else {
    const byLines = parseTemperatureGridByLines(text);
    // 실측 열화상은 보통 수백 행 이상. 메타 없이 tiny 텍스트는 거절.
    if (byLines.height < 2 || byLines.width < 2) {
      return null;
    }
    if (byLines.height < 100 && byLines.width < 100 && valueCount < 100) {
      return null;
    }
    dataMap = byLines.dataMap;
    width = byLines.width;
    height = byLines.height;
    mode = 'lines';
  }

  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let count = 0;
  for (let y = 0; y < dataMap.length; y += 1) {
    const row = dataMap[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x += 1) {
      const v = row[x];
      if (!Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
      count += 1;
    }
  }
  if (count === 0) {
    return null;
  }

  return {
    dataMap,
    width,
    height,
    mode,
    valueCount,
    min,
    max,
    average: sum / count
  };
}
